import { ref, computed } from '@vue/composition-api'
import useNotifications from '@/composables/useNotifications'
import usePermissionMatrix from '@/composables/usePermissionMatrix'
import {
  MODULE_NAV_KEYS, HEADER_ORDER, MODULE_HEADER_KEYS, ACTIONS,
} from '@/composables/permissionModules'
import i18n from '@/libs/i18n'
import { useLambda } from '@/views/habit/awsLambda'

export default function usePermissionsList() {
  const { showErrorMessage } = useNotifications()
  const { getItems } = useLambda()
  const { viewMode, setViewMode: setViewModeMatrix, toggleAction: toggleActionMatrix } = usePermissionMatrix()
  const isLoading = ref(true)
  const documents = ref([])
  const expanded = ref(HEADER_ORDER.reduce((acc, headerKey) => ({ ...acc, [headerKey]: false }), {}))

  const privilegeOrder = ['consultant', 'manager', 'supervisor', 'employee']

  const fetchPermissions = async () => {
    isLoading.value = true

    try {
      const items = await getItems({ collection: 'permission_default', query: {} })

      documents.value = privilegeOrder
        .map(privilege => items.find(item => item.privilege === privilege))
        .filter(Boolean)
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.permission_fetch_error'))
    } finally {
      isLoading.value = false
    }
  }

  // Qué módulos aparecen sale de Mongo (lo que exista en modules del documento), pero el
  // ORDEN de las filas se fuerza según permissionModules.js — así un módulo cargado a mano
  // en la base (ej. complaint-follow-up) no queda pegado al final solo por el orden en que
  // Mongo guardó las keys del documento, sino en su posición lógica del registro.
  const moduleKeys = computed(() => {
    const consultantDoc = documents.value.find(doc => doc.privilege === 'consultant')

    let keys
    if (consultantDoc?.modules) {
      keys = Object.keys(consultantDoc.modules)
    } else {
      const keySet = new Set()
      documents.value.forEach(doc => {
        Object.keys(doc.modules || {}).forEach(key => keySet.add(key))
      })
      keys = Array.from(keySet)
    }

    const registryOrder = Object.keys(MODULE_HEADER_KEYS)
    return [...keys].sort((a, b) => registryOrder.indexOf(a) - registryOrder.indexOf(b))
  })

  // One row per module. Each cell (one per privilege) carries all 5 actions —
  // whichever ones aren't in the DB yet (create/export/delete) resolve to null.
  const rows = computed(() => moduleKeys.value.map(moduleKey => ({
    moduleKey,
    moduleNavKey: MODULE_NAV_KEYS[moduleKey] || { key: moduleKey },
    headerKey: MODULE_HEADER_KEYS[moduleKey] || null,
    cells: privilegeOrder.map(privilege => {
      const doc = documents.value.find(d => d.privilege === privilege)
      const moduleEntry = doc?.modules?.[moduleKey] || {}
      return ACTIONS.reduce((acc, action) => {
        acc[action] = moduleEntry[action] || null
        return acc
      }, {})
    }),
  })))

  // Rows grouped by nav header, same order the sections appear in the sidebar
  const groups = computed(() => HEADER_ORDER
    .map(headerKey => ({ headerKey, rows: rows.value.filter(row => row.headerKey === headerKey) }))
    .filter(group => group.rows.length))

  const allExpanded = computed(() => Object.values(expanded.value).every(Boolean))

  const toggleAll = () => {
    const next = !allExpanded.value
    Object.keys(expanded.value).forEach(headerKey => { expanded.value[headerKey] = next })
  }

  // Reglas de edición (exclusión Ver todo/propio, Editar depende de Ver, guardado
  // optimista con revert) viven en usePermissionMatrix — compartidas con la futura
  // edición por worker (worker_permission_override). Acá solo se resuelve el `doc`/
  // `target` puntual de este screen (permission_default por privilegio) y se delega.
  const targetFor = privilege => ({ collection: 'permission_default', query: { privilege } })

  const setViewMode = (privilege, moduleKey, mode) => {
    const doc = documents.value.find(d => d.privilege === privilege)
    if (!doc) return
    setViewModeMatrix(doc, targetFor(privilege), moduleKey, mode)
  }

  const toggleAction = (privilege, moduleKey, action, cell) => {
    const doc = documents.value.find(d => d.privilege === privilege)
    if (!doc) return
    toggleActionMatrix(doc, targetFor(privilege), moduleKey, action, cell)
  }

  return {
    isLoading,
    documents,
    privilegeOrder,
    actions: ACTIONS,
    groups,
    expanded,
    allExpanded,
    toggleAll,
    fetchPermissions,
    viewMode,
    setViewMode,
    toggleAction,
  }
}
